/**
 * The simulator's thread. Turns are authored in place through AddMessage, customer on the
 * left and agent on the right as an agent sees a ticket, and any turn can be edited, deleted
 * or moved to the other side. The bottom bar holds the channel selector and Ask AI, with a
 * guidance line under it.
 */

import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { Sparkles, Bot, ShieldAlert } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import type { Message } from '@/modules/chatbot/types';
import type { Channel } from '@/types';
import type { ThinkingStep, RunApprovalTool } from '@/stores/websocket.store';
import { ThinkingTimeline } from './thinking-timeline';
import { AddMessage } from './add-message';
import { MessageRow, type ReplyView } from './message-row';
import { Composer } from './composer';
import { Guidance } from './guidance';
import { TurnBubble, TurnLabel, TurnRow } from '../ui/turn';
import type { SimulatorRole } from './roles';

interface SimulatorProps {
  messages: Message[];
  isLoading: boolean;
  thinkingStep?: string;
  thinkingSteps?: ThinkingStep[];
  approval?: RunApprovalTool[] | null;
  availableChannels?: Channel[];
  selectedChannelId?: string;
  onChannelChange?: (id: string) => void;
  onAddMessage?: (role: SimulatorRole, text: string) => void;
  onUpdateMessage?: (id: string, text: string) => void;
  onDeleteMessage?: (id: string) => void;
  onSwapMessageRole?: (id: string) => void;
  onAskAi?: () => void;
  /** Rendered under each AI reply; the thread says where a follow-up belongs, never what one is. */
  renderAfterReply?: (requestId: string) => ReactNode;
  /** Lets another surface present an AI reply in place. */
  replyView?: (requestId: string) => ReplyView | undefined;
  /** A second way into the empty thread, beside Add Message, and the panel it opens under the row. */
  emptyAction?: ReactNode;
  emptyPanel?: ReactNode;
  /** A described scenario is still landing: nothing is authored or asked until it has. */
  settingUp?: boolean;
}

const EmptyState = ({ describable }: { describable: boolean }) => (
  <div className='flex flex-col items-center justify-center gap-3 h-full text-center py-12 px-6'>
    <div className='h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center'>
      <Sparkles className='h-6 w-6 text-indigo-500' />
    </div>
    <div>
      <p className='text-sm font-medium'>Build a conversation, then ask AI</p>
      <p className='text-xs text-muted-foreground mt-1 max-w-sm'>
        Use <span className='font-medium'>Add Message</span> to author turns as Customer or Agent.
        Edit or delete any turn. When you're ready, hit{' '}
        <span className='font-medium'>Ask AI</span> to see what the AI would reply next.
        {describable && ' Or describe a situation and the scenario, its world and the conversation build themselves.'}
      </p>
    </div>
  </div>
);

export const Simulator = ({
  messages,
  isLoading,
  thinkingStep,
  thinkingSteps = [],
  approval,
  availableChannels = [],
  selectedChannelId,
  onChannelChange,
  onAddMessage,
  onUpdateMessage,
  onDeleteMessage,
  onSwapMessageRole,
  onAskAi,
  renderAfterReply,
  replyView,
  emptyAction,
  emptyPanel,
  settingUp = false,
}: SimulatorProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Keyed on what a new turn changes, not on the `messages` array, which is rebuilt every
  // render and would scroll the thread to the bottom on every unrelated re-render.
  const messageCount = messages.length;
  const lastMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messageCount, lastMessageId, isLoading]);

  const hasMessages = messages.length > 0;
  const lastNonErrorRole = [...messages].reverse().find((m) => !m.isError)?.role;

  return (
    <div className='flex flex-col h-full w-full mx-auto min-w-[300px] max-w-[760px]'>
      <div className='flex-1 overflow-hidden'>
        <ScrollArea ref={scrollAreaRef} className='h-full w-full **:data-[slot=scroll-area-scrollbar]:hidden'>
          <div className='px-4 py-5'>
            {!hasMessages ? (
              <div className='flex flex-col gap-4'>
                <EmptyState describable={Boolean(emptyAction)} />
                {!settingUp && (
                  <AddMessage onAdd={onAddMessage}>
                    <div className='flex justify-center gap-2'>
                      <AddMessage.Trigger />
                      {emptyAction}
                    </div>
                    <AddMessage.Editor />
                  </AddMessage>
                )}
                {emptyPanel}
              </div>
            ) : (
              <div className='flex flex-col gap-4'>
                {messages.map((m, i) => {
                  const isLast = i === messages.length - 1;
                  const replyRequestId = m.role === 'assistant' && !m.isError && m.requestId ? m.requestId : null;
                  // The finished reasoning goes above whichever outcome the run ended in: an AI
                  // reply, or an error or escalation bubble. A paused run has no message; its
                  // timeline renders with the approval card.
                  const showTimeline =
                    isLast && !isLoading && (replyRequestId !== null || m.isError) && thinkingSteps.length > 0;
                  return (
                    <Fragment key={m.id}>
                      {showTimeline && (
                        <ThinkingTimeline steps={thinkingSteps} live={false} />
                      )}
                      <MessageRow
                        message={m}
                        view={replyRequestId ? replyView?.(replyRequestId) : undefined}
                        isLast={isLast}
                        showActions
                        onUpdate={onUpdateMessage}
                        onDelete={onDeleteMessage}
                        onSwapRole={onSwapMessageRole}
                      />
                      {replyRequestId && renderAfterReply?.(replyRequestId)}
                    </Fragment>
                  );
                })}
                {isLoading && (
                  <>
                    {thinkingSteps.length > 0 && (
                      <ThinkingTimeline steps={thinkingSteps} live />
                    )}
                    <TurnRow side='right'>
                      <TurnLabel icon={Bot} className='text-indigo-600'>
                        <span className='font-medium'>AI · Agent</span>
                        <span>·</span>
                        <span className='animate-pulse'>{thinkingStep || 'Thinking…'}</span>
                      </TurnLabel>
                      <TurnBubble tone='agent' className='bg-indigo-50/50 min-w-[220px]'>
                        <Skeleton className='h-2.5 w-full mb-1.5 rounded-full bg-indigo-100' />
                        <Skeleton className='h-2.5 w-3/4 rounded-full bg-indigo-100' />
                      </TurnBubble>
                    </TurnRow>
                  </>
                )}
                {!isLoading && approval && approval.length > 0 && (
                  <>
                    {thinkingSteps.length > 0 && (
                      <ThinkingTimeline steps={thinkingSteps} live={false} />
                    )}
                    <TurnRow side='right'>
                      <TurnLabel icon={ShieldAlert} className='text-amber-600'>
                        <span className='font-medium'>Awaiting human approval</span>
                      </TurnLabel>
                      <TurnBubble tone='warning'>
                        <p>
                          The AI held {approval.length === 1 ? 'a write action' : `${approval.length} write actions`} for
                          human approval; nothing was executed.
                        </p>
                        <div className='mt-1.5 flex flex-col gap-1'>
                          {approval.map((t, i) => (
                            <span key={`${t.toolName}-${i}`} className='font-mono text-xs wrap-break-word'>
                              {t.toolName}
                              {t.args ? ` · ${t.args}` : ''}
                            </span>
                          ))}
                        </div>
                      </TurnBubble>
                    </TurnRow>
                  </>
                )}
                {!isLoading && !settingUp && (
                  <AddMessage onAdd={onAddMessage}>
                    <AddMessage.Trigger />
                    <AddMessage.Editor />
                  </AddMessage>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <div className='shrink-0 px-4 pb-4 pt-2'>
        <Composer
          disabled={isLoading || settingUp}
          lastNonErrorRole={lastNonErrorRole}
          channels={availableChannels}
          selectedChannelId={selectedChannelId}
          onChannelChange={onChannelChange}
          onAskAi={onAskAi}
        />
        <Guidance
          message={
            isLoading
              ? null
              : settingUp
                ? 'Setting up the scenario…'
                : lastNonErrorRole === 'assistant'
                  ? 'Last turn is the Agent — add a customer reply before asking AI'
                  : lastNonErrorRole === undefined
                    ? 'Add a customer message first'
                    : null
          }
        />
      </div>
    </div>
  );
};
